/* eslint-disable no-useless-escape */
import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionDetails,
  Grid,
  Box,
  Pagination,
} from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import DealsCard from "../DealsCard/DealsCard";
import {
  fetchProductAPIWithFilterFromWP,
  fetchProductSuggestionAPIWithFilterFromWP,
} from "src/api/service/services";

type Props = {
  dealsData: any[];
  total_items: number;
  filterArray: any;
  searchQuery: string;
  setOffset: CallableFunction;
  isDealsPage: boolean;
};

const ProductCardContainer: React.FC<Props> = ({
  dealsData,
  total_items,
  filterArray,
  searchQuery,
  setOffset,
  isDealsPage,
}) => {
  const [deals, setDeals] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(total_items);
  const recordsPerPage = 12;

  // Fetching deal cards
  useEffect(() => {
    setDeals(dealsData);
  }, [dealsData]);

  const fetchTopDeals = async (page: number) => {
    setLoading(true);
    const offset = (page - 1) * recordsPerPage; // Calculate offset
    try {
      const response = await fetchProductAPIWithFilterFromWP(
        filterArray,
        recordsPerPage,
        offset
      );
      setDeals(response.data.data); // Set the fetched deals
      setTotalItems(response?.data.total_items);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching deals:", error);
    } finally {
      setLoading(false);
    }
  };
  const fetchDeals = async (
    queryString: string,
    selectedFilters: any,
    limit: number,
    offset: number
  ) => {
    await fetchProductSuggestionAPIWithFilterFromWP(
      queryString,
      selectedFilters,
      limit,
      offset
    )
      .then((response) => {
        setDeals(response.data.data || []);
        setTotalItems(response?.data.total_items);
        setLoading(false);
      })
      .catch((error) => console.error(error));
  };
  // Function to fetch deals for a specific page
  const fetchDealsData = async (page: number) => {
    setLoading(true);
    const offset = (page - 1) * recordsPerPage; // Calculate offset
    try {
      await fetchDeals(searchQuery, filterArray, recordsPerPage, offset); // Fetch data from API
    } catch (error) {
      console.error("Error fetching deals:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {}, [totalItems]);

  // Trigger API call when component mounts or page changes
  useEffect(() => {
    if (currentPage !== 1) {
      if (isDealsPage) fetchTopDeals(currentPage);
      else fetchDealsData(currentPage);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };

  return (
    <Box>
      <Accordion defaultExpanded style={{ boxShadow: "none" }}>
        <AccordionDetails>
          {/* Responsive Grid for Deals Cards */}
          <Grid
            container
            spacing={2}
            sx={{
              flexGrow: 1,
              flexWrap: "wrap",
              justifyContent: "flex-start",
              height: "auto", // Avoid unnecessary stretching
            }}
          >
            {loading
              ? Array.from(new Array(recordsPerPage)).map((_, index) => (
                  <Grid
                    item
                    xs={6}
                    sm={4}
                    md={4}
                    lg={3}
                    xl={2}
                    key={index}
                    sx={{
                      display: "flex",
                      flexDirection: "column", // Adjust based on content
                      height: "auto", // Ensure the height is dynamic
                    }}
                  >
                    <Skeleton variant="rounded" height={300} width="100%" />
                  </Grid>
                ))
              : deals.map((deal, index) => (
                  <Grid
                    item
                    xs={6}
                    sm={4}
                    md={4}
                    lg={3}
                    xl={3} 
                    key={index}
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                    }}
                    gap={2}
                  >
                    <DealsCard
                      imageUrl={deal.product_images
                        ?.split("'")[1]
                        ?.replace(/[\[\]']/g, "")
                        ?.trim()}
                      title={deal.post_title}
                      price={deal.product_discounted_price}
                      originalPrice={deal.product_original_price}
                      retailerLogo={deal.retailer}
                      discount={deal.product_discount_percentage}
                      hoverText={"Save Upto"}
                      hoverPercentage={deal.product_discount_percentage}
                      id={deal.product_id}
                      productLink={deal.product_url_retailer}
                    />
                  </Grid>
                ))}
          </Grid>
          {/* Pagination Component */}
          <Box sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}>
            <Pagination
              count={Math.ceil(totalItems / recordsPerPage)}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              sx={{
                "& .Mui-selected": {
                  backgroundColor: "primary.main", // Customize the background color
                  color: "white", // Customize the text color
                  fontWeight: "bold", // Make it bold
                },
              }}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ProductCardContainer;
